import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import {
  formatAxiosAdminError,
  getAdminMotorHandbookCopy,
  getAdminMotorHandbookMedia,
  resolveBackendMediaUrl,
  saveAdminMotorHandbookCopy,
  saveAdminMotorHandbookMedia,
  uploadAdminMotorHandbookImage,
} from '@/api'
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure'
import { useHaptic } from '@/hooks/useHaptic'
import { buildDefaultMotorHandbookBody, motorHandbookCopyTemplate } from '@/lib/motorHandbookCopy'
import { parseMotorHandbookBulkLines } from '@/lib/motorHandbookBulkImport'
import {
  MOTOR_HANDBOOK_SECTIONS,
  buildMotorHandbookFlatIndex,
  motorHandbookModelKey,
  type MotorHandbookModel,
} from '@/lib/motorTechReference'

type Props = {
  catalogWrite: boolean
  token: string | null
}

type MediaFilter = 'all' | 'with' | 'without'

const BULK_PLACEHOLDER = `# Одна строка — идентификатор и URL (через запятую, таб или =)
razdvizhnye.mr-2234, https://example.com/motor.jpg
MR-2234, https://example.com/mr2234.jpg
razdvizhnye|MR-2234, https://example.com/mr2234.jpg`

const COPY_PLACEHOLDER = `Управление и подключение:
• Пульт управления: По радиоканалу (433МГц)
• Порт RJ9: «Сухие контакты»

Внешний вид и конструкция:
• Цвет: Белый

Производительность:
• Мощность: 65 Вт

Надежность и защита:
• IP40

Что входит в комплект:
• Мотор, кабель, инструкция`

function handbookModelByKey(key: string): MotorHandbookModel | null {
  for (const section of MOTOR_HANDBOOK_SECTIONS) {
    for (const model of section.models) {
      if (motorHandbookModelKey(section.id, model.name) === key) return model
    }
  }
  return null
}

export function MotorHandbookMediaAdmin({ catalogWrite, token }: Props) {
  const haptic = useHaptic()
  const flatIndex = useMemo(() => buildMotorHandbookFlatIndex(), [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<Record<string, string>>({})
  const [copyItems, setCopyItems] = useState<Record<string, string>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [savingCopyKey, setSavingCopyKey] = useState<string | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [sectionFilter, setSectionFilter] = useState<string>('all')
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all')
  const [bulkText, setBulkText] = useState('')
  const [bulkPreview, setBulkPreview] = useState<ReturnType<typeof parseMotorHandbookBulkLines> | null>(null)
  const [urlDrafts, setUrlDrafts] = useState<Record<string, string>>({})
  const [copyDrafts, setCopyDrafts] = useState<Record<string, string>>({})

  const reload = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [data, copy] = await Promise.all([getAdminMotorHandbookMedia(), getAdminMotorHandbookCopy()])
      setItems(data || {})
      setCopyItems(copy || {})
    } catch (e) {
      setItems({})
      setCopyItems({})
      setError(formatAxiosAdminError(e, 'Не удалось загрузить карточки моторов.'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void reload()
  }, [reload])

  const stats = useMemo(() => {
    const total = flatIndex.length
    let withImage = 0
    let withCopy = 0
    for (const e of flatIndex) {
      if (String(items[e.key] || '').trim()) withImage += 1
      if (String(copyItems[e.key] || '').trim()) withCopy += 1
    }
    return { total, withImage, without: total - withImage, withCopy }
  }, [flatIndex, items, copyItems])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return flatIndex.filter((e) => {
      if (sectionFilter !== 'all' && e.sectionId !== sectionFilter) return false
      const has = Boolean(String(items[e.key] || '').trim())
      if (mediaFilter === 'with' && !has) return false
      if (mediaFilter === 'without' && has) return false
      if (!q) return true
      const hay = `${e.modelName} ${e.sectionTitle} ${e.key}`.toLowerCase()
      return hay.includes(q)
    })
  }, [flatIndex, items, search, sectionFilter, mediaFilter])

  const persistItems = async (next: Record<string, string>) => {
    const saved = await saveAdminMotorHandbookMedia(next)
    setItems(saved || {})
    haptic.selection()
  }

  const onBulkPreview = () => {
    const parsed = parseMotorHandbookBulkLines(bulkText)
    setBulkPreview(parsed)
    haptic.selection()
  }

  const onBulkApply = async () => {
    if (!catalogWrite || !bulkPreview) return
    setBulkSaving(true)
    try {
      const merged = { ...items, ...bulkPreview.updates }
      await persistItems(merged)
      setBulkPreview(null)
      setBulkText('')
    } catch (e) {
      alert(formatAxiosAdminError(e, 'Не удалось сохранить массовый импорт.'))
    } finally {
      setBulkSaving(false)
    }
  }

  const saveCopyForKey = async (key: string) => {
    if (!catalogWrite) return
    const text = (copyDrafts[key] ?? copyItems[key] ?? '').trim()
    setSavingCopyKey(key)
    try {
      const next = { ...copyItems }
      if (text) next[key] = text
      else delete next[key]
      const saved = await saveAdminMotorHandbookCopy(next)
      setCopyItems(saved || {})
      setCopyDrafts((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
      haptic.selection()
    } catch (e) {
      alert(formatAxiosAdminError(e, 'Не удалось сохранить текст.'))
    } finally {
      setSavingCopyKey(null)
    }
  }

  const saveUrlForKey = async (key: string) => {
    if (!catalogWrite) return
    const url = (urlDrafts[key] ?? items[key] ?? '').trim()
    setSavingKey(key)
    try {
      const next = { ...items }
      if (url) next[key] = url
      else delete next[key]
      await persistItems(next)
      setUrlDrafts((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
    } catch (e) {
      alert(formatAxiosAdminError(e, 'Не удалось сохранить URL.'))
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <AdminGlassDisclosure
      accent="cyan"
      defaultOpen={true}
      density="compact"
      title="Карточки моторов (фото + текст)"
      subtitle="Фото продукта и ТТХ текстом для «Товары → Моторы»"
      badge={`${stats.withImage}/${stats.total}`}
    >
      <div
        className="rounded-xl px-3 py-2 mb-3 text-[10px] leading-relaxed"
        style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid rgba(99,179,237,0.35)', color: 'var(--text-2)' }}
      >
        <strong>Фото</strong> — только мотор. <strong>Текст</strong> — как эталон MR-2234: 5 секций
        («Управление и подключение:» …), внутри подзаголовки («Пульт управления:») и пункты «•». На аксессуары не
        влияет.
      </div>
      {error ? (
        <div
          className="rounded-xl px-3 py-2 text-xs mb-3"
          style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
        >
          {error}
          <button type="button" className="underline ml-2 press" onClick={() => void reload()}>
            Обновить
          </button>
        </div>
      ) : null}

      <div
        className="rounded-xl px-3 py-2 mb-3 flex flex-wrap gap-2 text-[10px]"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
      >
        <span style={{ color: 'var(--text-2)' }}>
          Всего: <strong>{stats.total}</strong>
        </span>
        <span style={{ color: '#68D391' }}>
          С картинкой: <strong>{stats.withImage}</strong>
        </span>
        <span style={{ color: 'var(--text-3)' }}>
          Без фото: <strong>{stats.without}</strong>
        </span>
        <span style={{ color: 'var(--cyan-bright)' }}>
          С текстом: <strong>{stats.withCopy}</strong>
        </span>
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" style={{ color: 'var(--text-3)' }} />
          <input
            className="input-glass w-full pl-8 pr-2 py-2 rounded-lg text-xs"
            placeholder="Поиск по модели, секции или ключу…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="input-glass px-2 py-1.5 rounded-lg text-[10px] flex-1 min-w-[120px]"
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
          >
            <option value="all">Все секции</option>
            {MOTOR_HANDBOOK_SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
          <select
            className="input-glass px-2 py-1.5 rounded-lg text-[10px] flex-1 min-w-[100px]"
            value={mediaFilter}
            onChange={(e) => setMediaFilter(e.target.value as MediaFilter)}
          >
            <option value="all">Все</option>
            <option value="with">Только с URL</option>
            <option value="without">Без картинки</option>
          </select>
        </div>
        <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
          Показано: {filtered.length} из {stats.total}
        </p>
      </div>

      {catalogWrite ? (
        <div
          className="rounded-xl p-3 mb-3 space-y-2"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,179,237,0.25)' }}
        >
          <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
            Массовый импорт URL
          </p>
          <textarea
            className="input-glass w-full px-2 py-2 rounded-lg text-[10px] font-mono min-h-[100px]"
            placeholder={BULK_PLACEHOLDER}
            value={bulkText}
            onChange={(e) => {
              setBulkText(e.target.value)
              setBulkPreview(null)
            }}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="press px-3 py-1.5 rounded-lg text-[10px] font-bold"
              style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan)', border: '1px solid rgba(99,179,237,0.35)' }}
              disabled={!bulkText.trim()}
              onClick={onBulkPreview}
            >
              Разобрать
            </button>
            <button
              type="button"
              className="press px-3 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-40"
              style={{ background: 'rgba(72,187,120,0.22)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
              disabled={!bulkPreview?.report.applied.length || bulkSaving}
              onClick={() => void onBulkApply()}
            >
              {bulkSaving ? 'Сохранение…' : `Применить (${bulkPreview?.report.applied.length ?? 0})`}
            </button>
          </div>
          {bulkPreview ? (
            <div className="text-[10px] space-y-1" style={{ color: 'var(--text-2)' }}>
              <p>
                Готово к записи: <strong>{bulkPreview.report.applied.length}</strong>
                {bulkPreview.report.ambiguous.length ? (
                  <> · неоднозначно: {bulkPreview.report.ambiguous.length}</>
                ) : null}
                {bulkPreview.report.unknown.length ? <> · не найдено: {bulkPreview.report.unknown.length}</> : null}
                {bulkPreview.report.invalid.length ? <> · ошибка URL: {bulkPreview.report.invalid.length}</> : null}
              </p>
              {bulkPreview.report.ambiguous.slice(0, 3).map((a) => (
                <p key={a.line} style={{ color: '#FC814A' }}>
                  стр. {a.line}: несколько ключей — уточните секцию (секция|модель)
                </p>
              ))}
              {bulkPreview.report.unknown.slice(0, 3).map((u) => (
                <p key={u.line} style={{ color: 'var(--text-3)' }}>
                  стр. {u.line}: модель не найдена
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin" size={20} style={{ color: 'var(--cyan)' }} />
        </div>
      ) : (
        <div className="space-y-3 max-h-[min(70vh,520px)] overflow-y-auto pr-1">
          {filtered.map((entry) => {
            const imageUrl = resolveBackendMediaUrl(items[entry.key] || '')
            const saving = savingKey === entry.key
            const savingCopy = savingCopyKey === entry.key
            const urlValue = urlDrafts[entry.key] ?? items[entry.key] ?? ''
            const copyValue = copyDrafts[entry.key] ?? copyItems[entry.key] ?? ''
            const handbookModel = handbookModelByKey(entry.key)
            return (
              <div
                key={entry.key}
                className="rounded-lg p-2"
                style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)' }}
              >
                <p className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                  {entry.sectionTitle}
                </p>
                <p className="text-[11px] font-semibold" style={{ color: 'var(--text-1)' }}>
                  {entry.modelName}
                </p>
                <p className="text-[9px] font-mono mt-0.5 break-all" style={{ color: 'var(--text-3)' }}>
                  {entry.key}
                </p>
                {imageUrl ? (
                  <img
                    key={imageUrl}
                    src={imageUrl}
                    alt={entry.modelName}
                    className="mt-1.5 w-full max-h-28 object-contain rounded-md"
                    style={{ background: '#fff' }}
                  />
                ) : (
                  <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-3)' }}>
                    Изображение не задано
                  </p>
                )}
                {catalogWrite ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-1">
                      <input
                        className="input-glass flex-1 px-2 py-1.5 rounded-lg text-[10px]"
                        placeholder="https://… URL фото мотора"
                        value={urlValue}
                        disabled={saving}
                        onChange={(e) => setUrlDrafts((prev) => ({ ...prev, [entry.key]: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="press px-2 py-1.5 rounded-lg text-[10px] font-bold shrink-0 disabled:opacity-40"
                        style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan)', border: '1px solid rgba(99,179,237,0.35)' }}
                        disabled={saving}
                        onClick={() => void saveUrlForKey(entry.key)}
                      >
                        URL
                      </button>
                    </div>
                    <label className="text-[10px] font-bold block" style={{ color: 'var(--text-3)' }}>
                      Или файл
                      <input
                        type="file"
                        accept="image/*"
                        className="mt-1 block w-full text-[10px]"
                        disabled={saving}
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          setSavingKey(entry.key)
                          try {
                            const uploadedMap = await uploadAdminMotorHandbookImage(entry.key, file)
                            setItems(uploadedMap || {})
                            haptic.selection()
                          } catch (err) {
                            alert(formatAxiosAdminError(err, 'Не удалось загрузить фото мотора.'))
                          } finally {
                            setSavingKey(null)
                            e.target.value = ''
                          }
                        }}
                      />
                    </label>
                    {saving ? <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>Сохранение…</p> : null}
                    <div
                      className="pt-2 mt-2 space-y-2"
                      style={{ borderTop: '1px solid var(--border-subtle)' }}
                    >
                      <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
                        Текст ТТХ
                      </p>
                      <textarea
                        className="input-glass w-full px-2 py-2 rounded-lg text-[10px] leading-relaxed min-h-[120px]"
                        placeholder={COPY_PLACEHOLDER}
                        value={copyValue}
                        disabled={savingCopy}
                        onChange={(e) =>
                          setCopyDrafts((prev) => ({ ...prev, [entry.key]: e.target.value }))
                        }
                      />
                      <div className="flex flex-wrap gap-2">
                        {handbookModel ? (
                          <>
                            <button
                              type="button"
                              className="press px-2 py-1.5 rounded-lg text-[10px] font-semibold"
                              style={{
                                background: 'rgba(255,255,255,0.06)',
                                color: 'var(--text-2)',
                                border: '1px solid var(--border-subtle)',
                              }}
                              disabled={savingCopy}
                              onClick={() => {
                                setCopyDrafts((prev) => ({
                                  ...prev,
                                  [entry.key]: motorHandbookCopyTemplate(entry.modelName),
                                }))
                                haptic.selection()
                              }}
                            >
                              Шаблон секций
                            </button>
                            <button
                              type="button"
                              className="press px-2 py-1.5 rounded-lg text-[10px] font-semibold"
                              style={{
                                background: 'rgba(255,255,255,0.06)',
                                color: 'var(--text-2)',
                                border: '1px solid var(--border-subtle)',
                              }}
                              disabled={savingCopy}
                              onClick={() => {
                                const draft = buildDefaultMotorHandbookBody(handbookModel)
                                setCopyDrafts((prev) => ({ ...prev, [entry.key]: draft }))
                                haptic.selection()
                              }}
                            >
                              Из справочника
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="press px-2 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-40"
                          style={{
                            background: 'rgba(99,179,237,0.2)',
                            color: 'var(--cyan)',
                            border: '1px solid rgba(99,179,237,0.35)',
                          }}
                          disabled={savingCopy}
                          onClick={() => void saveCopyForKey(entry.key)}
                        >
                          {savingCopy ? 'Сохранение…' : 'Сохранить текст'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
          {!filtered.length ? (
            <p className="text-center text-xs py-6" style={{ color: 'var(--text-3)' }}>
              Ничего не найдено
            </p>
          ) : null}
        </div>
      )}
    </AdminGlassDisclosure>
  )
}
